import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  LocationOn as LocationOnIcon,
  CreditCard as CreditCardIcon,
  Info as InfoIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShippingOutlined as ShippedIcon,
  Done as DeliveredIcon,
  RadioButtonUnchecked as PendingIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatLongDate } from '../../../utils/dateUtils';

// Order interface'ini import etmek yerine burada tanımlayalım
interface OrderItem {
  productId: string;
  name: string;
  image: string;
  price: number;
  quantity: number;
}

interface ShippingAddress {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  district: string;
  phone: string;
  title: string;
}

interface PaymentInfo {
  method: string;
  status: string;
  dekontId?: string;
  transactionId?: string;
  paymentDate?: string;
}

interface ParamPosData {
  dekontId?: string;
  islemId?: string;
  sanalPosIslemId?: string;
  sonuc?: string;
  sonucStr?: string;
  tahsilatTutari?: string;
  odemeTutari?: string;
  siparisTarih?: string;
  // ✅ Ek ParamPOS alanları
  guid?: string;
  islemTarih?: string;
  extData?: string;
  bankaSonucKod?: string;
  pb?: string;
  kkNo?: string;
  taksit?: string;
  hash?: string;
  islemGuid?: string;
  // Backend'den gelebilecek alternative field names
  TURKPOS_RETVAL_GUID?: string;
  TURKPOS_RETVAL_Islem_Tarih?: string;
  TURKPOS_RETVAL_Ext_Data?: string;
  TURKPOS_RETVAL_Banka_Sonuc_Kod?: string;
  TURKPOS_RETVAL_PB?: string;
  TURKPOS_RETVAL_KK_No?: string;
  TURKPOS_RETVAL_Taksit?: string;
  TURKPOS_RETVAL_Hash?: string;
  TURKPOS_RETVAL_Islem_GUID?: string;
}

interface Order {
  id: string;
  orderId: string;
  orderNumber: string;
  memberId: string;
  memberName?: string;
  memberEmail?: string;
  isGuest?: boolean;
  date: string;
  updatedAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  status: 'received' | 'pending' | 'paid' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled' | 'cancel_requested';
  totalAmount: number;
  // TRY dışı mağazada sipariş anındaki kur snapshot'ı ve müşteriden çekilen TL karşılığı
  appliedExchangeRate?: number | null;
  totalAmountTl?: number | null;
  /** Sipariş tutarlarının para birimi (null = eski kayıt → TL varsayılır) */
  currency?: string | null;
  itemsTotal?: number;
  items: OrderItem[];
  shippingAddress?: ShippingAddress | any;
  billingAddress?: any;
  paymentInfo?: PaymentInfo;
  trackingNumber?: string;
  shippingCompany?: string;
  /** Firmalar arası B2B sevk irsaliyesi numarası (sevkiyatta otomatik kesilir; B2C'de boş) */
  waybillNumber?: string | null;
  isInterCompanyB2B?: boolean;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  eInvoiceStatus?: string | null;
  eInvoiceEttn?: string | null;
  paymentBreakdownMode?: string | null;
  receiptStatus?: string | null;
  hasReceiptDiscrepancy?: boolean;
  // ParamPOS Bilgileri
  dekontId?: string;
  islemId?: string;
  paramPosData?: string; // JSON string
  cancelRequest?: {
    reason: string;
    requestDate: string;
    requestedBy: string;
    adminResponse?: {
      rejected?: boolean;
      reason: string;
      rejectedAt?: string;
      respondedAt?: string;
      rejectedBy?: string;
    };
  };
  // N11 özel alanları
  marketplace?: string;
  n11OrderId?: string;
  n11Status?: string;
  customerName?: string;
  customerEmail?: string;
  shipmentPackageStatus?: string;
  packageHistories?: Array<{
    createdDate: number;
    status: string;
  }>;
  cargoInfo?: {
    cargoSenderNumber?: string;
    cargoTrackingNumber?: string;
    cargoTrackingLink?: string;
    shipmentCompanyId?: string;
    cargoProviderName?: string;
    shipmentMethod?: number;
  };
}

interface OrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  order: Order | null;
  getStatusChip: (status: string) => React.ReactElement;
}

const ORANGE = '#f57c00';

const parseJsonField = (raw: any) => {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

const normalizeAddress = (raw: any) => {
  const obj = parseJsonField(raw);
  if (!obj || typeof obj !== 'object') return null;
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = obj[k] ?? obj[k.charAt(0).toUpperCase() + k.slice(1)];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
    }
    return '';
  };
  const firstLast = `${pick('firstName')} ${pick('lastName')}`.trim();
  return {
    name: pick('fullName', 'name', 'recipientName') || firstLast,
    title: pick('title'),
    address: pick('address', 'fullAddress', 'address1', 'addressLine'),
    city: pick('city', 'cityName'),
    district: pick('district', 'town', 'townName', 'county'),
    neighborhood: pick('neighborhood', 'districtName'),
    phone: pick('phone', 'gsm', 'phoneNumber'),
    postalCode: pick('postalCode', 'zipCode'),
    taxNumber: pick('taxNumber', 'taxId', 'vkn'),
    taxOffice: pick('taxOffice', 'taxHouse'),
    tcId: pick('tcId', 'identityNo'),
  };
};

// E-ticaret sipariş durumu → renk (Paket Geçmişi noktaları için)
const statusFlowColor = (status: string, done: boolean): string => {
  if (!done) return '#bdbdbd';
  const s = String(status || '').toLowerCase();
  if (s === 'cancelled' || s === 'cancel_requested') return '#d32f2f';
  if (s === 'delivered') return '#2e7d32';
  if (s === 'shipped') return '#0288d1';
  return '#2e7d32';
};

const statusFlowIcon = (status: string, done: boolean) => {
  if (!done) return <PendingIcon sx={{ color: '#fff', fontSize: 18 }} />;
  const s = String(status || '').toLowerCase();
  if (s === 'shipped') return <ShippedIcon sx={{ color: '#fff', fontSize: 18 }} />;
  if (s === 'delivered') return <DeliveredIcon sx={{ color: '#fff', fontSize: 18 }} />;
  if (s === 'cancelled' || s === 'cancel_requested')
    return <CancelIcon sx={{ color: '#fff', fontSize: 18 }} />;
  return <CheckCircleIcon sx={{ color: '#fff', fontSize: 18 }} />;
};

// Sipariş durumundan Paket Geçmişi (timeline) türet
const buildTimeline = (order: Order) => {
  const status = String(order.status || '').toLowerCase();
  const isCancelled = status === 'cancelled' || status === 'cancel_requested';

  if (isCancelled) {
    return [
      { status: 'created', label: 'Sipariş Oluşturuldu', date: order.date, done: true },
      {
        status,
        label: status === 'cancel_requested' ? 'İptal Talebi Alındı' : 'Sipariş İptal Edildi',
        date: order.cancelledAt || order.updatedAt,
        done: true,
      },
    ];
  }

  const reachedConfirmed = ['confirmed', 'shipped', 'delivered'].includes(status);
  const reachedShipped = ['shipped', 'delivered'].includes(status);
  const reachedDelivered = status === 'delivered';

  return [
    { status: 'created', label: 'Sipariş Oluşturuldu', date: order.date, done: true },
    { status: 'confirmed', label: 'Sipariş Onaylandı / Hazırlanıyor', date: undefined, done: reachedConfirmed },
    { status: 'shipped', label: 'Sipariş Kargolandı', date: undefined, done: reachedShipped },
    { status: 'delivered', label: 'Sipariş Teslim Edildi', date: undefined, done: reachedDelivered },
  ];
};

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ 
  open, 
  onClose, 
  order, 
  getStatusChip 
}) => {
  const { t } = useTranslation('salesSystem');
  const [showParamPosDetails, setShowParamPosDetails] = useState(false);
  // Hook'lar erken return'den önce, koşulsuz çağrılmalı
  const timeline = useMemo(() => (order ? buildTimeline(order) : []), [order]);

  if (!order) return null;

  // Sipariş para birimi snapshot'ı: TRY dışı (örn. bayi USD) siparişte ₺ yanıltıcı olmasın
  const CURRENCY_SYMBOLS: Record<string, string> = { TRY: '₺', TL: '₺', USD: '$', EUR: '€', GBP: '£' };
  const orderCurrencyCode = (order.currency || 'TRY').toUpperCase();
  const orderCurrencySymbol = CURRENCY_SYMBOLS[orderCurrencyCode] || `${orderCurrencyCode} `;

  // ParamPOS verisini parse et
  let parsedParamPosData: ParamPosData | null = null;
  if (order.paramPosData) {
    try {
      parsedParamPosData = JSON.parse(order.paramPosData);
    } catch (error) {
      console.error('[ORDER DETAILS MODAL] ParamPOS verisi parse edilemedi:', error);
    }
  }

  // Shipping Address'i parse et
  let shippingAddr = order.shippingAddress;
  if (typeof shippingAddr === 'string' && shippingAddr) {
    try {
      shippingAddr = JSON.parse(shippingAddr);
    } catch (error) {
      console.error('[ORDER DETAILS MODAL] Shipping Address parse edilemedi:', error);
      shippingAddr = null;
    }
  }

  // Kart düzeni için normalize edilmiş adresler + türetilmiş değerler
  const shipping = normalizeAddress(order.shippingAddress);
  const billing = normalizeAddress(order.billingAddress);

  const itemsTotal =
    order.itemsTotal ??
    order.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shippingFee = Math.max(0, (order.totalAmount || 0) - itemsTotal);

  const parsedCargo = parseJsonField(order.cargoInfo) as any;
  const cargoCompany = order.shippingCompany || parsedCargo?.cargoProviderName || '';
  const trackingNumber = order.trackingNumber || parsedCargo?.cargoTrackingNumber || '';
  const trackingLink = parsedCargo?.cargoTrackingLink || '';
  const hasCargo = Boolean(cargoCompany || trackingNumber || trackingLink);

  const renderAddressCard = (
    addr: ReturnType<typeof normalizeAddress>,
    title: string,
    icon: React.ReactNode,
    showInvoiceExtras = false
  ) => {
    if (!addr || (!addr.name && !addr.address && !addr.city)) return null;
    return (
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {icon} {title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {addr.name && <Typography variant="body2" fontWeight="medium">{addr.name}</Typography>}
            {addr.title && (
              <Typography variant="body2" color="text.secondary">{addr.title}</Typography>
            )}
            {addr.address && <Typography variant="body2">{addr.address}</Typography>}
            {(addr.neighborhood || addr.district || addr.city) && (
              <Typography variant="body2">
                {addr.neighborhood && addr.neighborhood !== addr.district ? `${addr.neighborhood}, ` : ''}
                {[addr.district, addr.city].filter(Boolean).join(', ')}
              </Typography>
            )}
            {addr.postalCode && (
              <Typography variant="body2" color="text.secondary">
                {t('ecommerceOrders.orderDetailsModal.postalCode')}: {addr.postalCode}
              </Typography>
            )}
            {addr.phone && <Typography variant="body2" color="text.secondary">{addr.phone}</Typography>}
            {showInvoiceExtras && addr.taxNumber && (
              <Typography variant="body2" color="text.secondary">Vergi No: {addr.taxNumber}</Typography>
            )}
            {showInvoiceExtras && addr.taxOffice && (
              <Typography variant="body2" color="text.secondary">Vergi Dairesi: {addr.taxOffice}</Typography>
            )}
            {showInvoiceExtras && addr.tcId && (
              <Typography variant="body2" color="text.secondary">TC: {addr.tcId}</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShoppingCartIcon color="primary" />
          <Box>
            <Typography variant="h6">
              {t('ecommerceOrders.orderDetailsModal.title', { orderNumber: order.orderNumber })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.customerName || order.memberName || t('ecommerceOrders.table.customerNameUnknown')} - {order.customerEmail || order.memberEmail || t('ecommerceOrders.table.emailUnknown')}
              {order.isGuest && (
                <Chip label="Misafir" size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
              )}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Sipariş Bilgileri */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <CalendarTodayIcon sx={{ mr: 1, fontSize: 16 }} />
                  {t('ecommerceOrders.orderDetailsModal.orderInformation')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.orderNumber', 'Sipariş No')}:</Typography>
                    <Typography variant="body2" fontWeight="medium">{order.orderNumber}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.date')}:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatLongDate(order.date)}
                    </Typography>
                  </Box>
                  {order.updatedAt && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.lastUpdate', 'Son Güncelleme')}:</Typography>
                      <Typography variant="body2">{formatLongDate(order.updatedAt)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.status')}:</Typography>
                    {getStatusChip(order.status)}
                  </Box>
                  {order.cancelReason && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.cancelReason', 'İptal Nedeni')}:</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right' }}>{order.cancelReason}</Typography>
                    </Box>
                  )}
                  {order.invoiceNumber && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.invoiceNumber')}:</Typography>
                      <Typography variant="body2">{order.invoiceNumber}</Typography>
                    </Box>
                  )}
                  {order.isInterCompanyB2B && order.invoiceId && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.eInvoice')}:</Typography>
                      <Typography variant="body2">
                        {['SENT', 'APPROVED'].includes((order.eInvoiceStatus || '').toUpperCase())
                          ? `${order.eInvoiceStatus}${order.eInvoiceEttn ? ` · ${order.eInvoiceEttn}` : ''}`
                          : t('ecommerceOrders.orderDetailsModal.eInvoiceDraft')}
                      </Typography>
                    </Box>
                  )}
                  {order.isInterCompanyB2B && (order.paymentBreakdownMode || '').toLowerCase() === 'online' && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.payment')}:</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right' }}>
                        {t('ecommerceOrders.orderDetailsModal.paymentOnline')}
                      </Typography>
                    </Box>
                  )}
                  {order.isInterCompanyB2B && (order.paymentBreakdownMode || '').toLowerCase() === 'cari' && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.payment')}:</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right' }}>
                        {t('ecommerceOrders.orderDetailsModal.paymentAccount')}
                      </Typography>
                    </Box>
                  )}
                  {order.receiptStatus && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.receipt')}:</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right' }}>
                        {order.hasReceiptDiscrepancy
                          ? t('ecommerceOrders.orderDetailsModal.receiptDiscrepancy')
                          : t('ecommerceOrders.orderDetailsModal.receiptApproved')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Fiyat Bilgileri */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                  {t('ecommerceOrders.orderDetailsModal.priceInformation', 'Fiyat Bilgileri')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.productsTotal')}:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {orderCurrencySymbol}{itemsTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.shippingFee')}:</Typography>
                    <Typography variant="body2" fontWeight="medium" color={shippingFee > 0 ? 'inherit' : 'success.main'}>
                      {shippingFee > 0
                        ? `${orderCurrencySymbol}${shippingFee.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                        : t('ecommerceOrders.orderDetailsModal.free')}
                    </Typography>
                  </Box>
                  {(order.appliedExchangeRate ?? 0) > 0 && order.appliedExchangeRate !== 1 && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.appliedRate')}:</Typography>
                        <Typography variant="body2">
                          {(order.appliedExchangeRate || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ₺
                        </Typography>
                      </Box>
                      {(order.totalAmountTl ?? 0) > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.paidTl')}:</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            ₺{(order.totalAmountTl || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" fontWeight="bold">{t('ecommerceOrders.orderDetailsModal.totalAmount')}:</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: ORANGE }}>
                      {orderCurrencySymbol}{order.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Kargo Bilgileri */}
          {hasCargo && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <LocalShippingIcon sx={{ mr: 1, fontSize: 16 }} />
                    {t('ecommerceOrders.orderDetailsModal.shippingInformation', 'Kargo Bilgileri')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.cargoCompany', 'Kargo Firması')}:</Typography>
                      <Typography variant="body2" fontWeight="medium">{cargoCompany || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.trackingNumber', 'Takip Numarası')}:</Typography>
                      <Typography variant="body2" fontFamily="monospace">{trackingNumber || '-'}</Typography>
                    </Box>
                    {trackingLink && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.trackingLink', 'Takip Linki')}:</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(trackingLink, '_blank')}
                          sx={{ fontSize: '0.75rem', color: ORANGE, borderColor: ORANGE }}
                        >
                          {t('ecommerceOrders.orderDetailsModal.track', 'Takip Et')}
                        </Button>
                      </Box>
                    )}
                    {order.waybillNumber && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.waybillNumber', 'Sevk İrsaliyesi')}:</Typography>
                        <Typography variant="body2" fontFamily="monospace">{order.waybillNumber}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Paket Geçmişi */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <TimelineIcon sx={{ mr: 1, fontSize: 16 }} />
                  {t('ecommerceOrders.orderDetailsModal.packageHistory', 'Paket Geçmişi')}
                </Typography>
                <List sx={{ p: 0 }}>
                  {timeline.map((step, index) => (
                    <ListItem key={`${step.status}-${index}`} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: statusFlowColor(step.status, step.done),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {statusFlowIcon(step.status, step.done)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ color: step.done ? 'inherit' : 'text.disabled' }}
                          >
                            {step.label}
                          </Typography>
                        }
                        secondary={
                          step.date && step.done ? (
                            <Typography variant="caption" color="text.secondary">
                              {formatLongDate(step.date)}
                            </Typography>
                          ) : undefined
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Ödeme Bilgileri */}
          {order.paymentInfo && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <CreditCardIcon sx={{ mr: 1, fontSize: 16 }} />
                    {t('ecommerceOrders.orderDetailsModal.paymentInformation')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.method')}:</Typography>
                      <Typography variant="body2">{t('ecommerceOrders.orderDetailsModal.creditCard')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.status')}:</Typography>
                      <Chip label={t('ecommerceOrders.orderDetailsModal.completed')} color="success" size="small" />
                    </Box>
                    {order.paymentInfo.dekontId && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.receiptId')}:</Typography>
                        <Typography variant="body2" fontFamily="monospace">
                          {order.paymentInfo.dekontId}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* ParamPOS Ödeme Detayları */}
          {(order.dekontId || order.islemId || parsedParamPosData) && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ bgcolor: 'success.50', borderColor: 'success.main' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'success.dark' }}>
                    <CreditCardIcon sx={{ mr: 1, fontSize: 16 }} />
                    {t('ecommerceOrders.orderDetailsModal.paramPosPaymentDetails')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(order.dekontId || parsedParamPosData?.dekontId) && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.receiptId')}:</Typography>
                        <Typography variant="body2" fontFamily="monospace" fontWeight="medium">
                          {order.dekontId || parsedParamPosData?.dekontId}
                        </Typography>
                      </Box>
                    )}
                    {(order.islemId || parsedParamPosData?.islemId) && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.transactionId')}:</Typography>
                        <Typography variant="body2" fontFamily="monospace" fontWeight="medium">
                          {order.islemId || parsedParamPosData?.islemId}
                        </Typography>
                      </Box>
                    )}
                    {parsedParamPosData?.sonuc && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('ecommerceOrders.orderDetailsModal.result')}:</Typography>
                        <Chip 
                          label={parsedParamPosData.sonuc === '1' ? t('ecommerceOrders.orderDetailsModal.successful') : t('ecommerceOrders.orderDetailsModal.error')} 
                          color={parsedParamPosData.sonuc === '1' ? 'success' : 'error'} 
                          size="small" 
                        />
                      </Box>
                    )}
                    
                    {/* Show All Details Button */}
                    {order.paramPosData && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          startIcon={<InfoIcon />}
                          onClick={() => setShowParamPosDetails(true)}
                          sx={{ 
                            borderColor: 'success.main',
                            color: 'success.dark',
                            '&:hover': {
                              borderColor: 'success.dark',
                              bgcolor: 'success.50'
                            }
                          }}
                        >
                          {t('ecommerceOrders.orderDetailsModal.showAllParamPosDetails')}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Teslimat Adresi */}
          {shipping && (
            <Grid item xs={12} md={6}>
              {renderAddressCard(
                shipping,
                t('ecommerceOrders.orderDetailsModal.deliveryAddress'),
                <LocationOnIcon sx={{ mr: 1, fontSize: 16 }} />
              )}
            </Grid>
          )}

          {/* Fatura Adresi */}
          {billing && (
            <Grid item xs={12} md={6}>
              {renderAddressCard(
                billing,
                t('ecommerceOrders.orderDetailsModal.billingAddress', 'Fatura Adresi'),
                <ReceiptIcon sx={{ mr: 1, fontSize: 16 }} />,
                true
              )}
            </Grid>
          )}

          {/* Adres bulunamadı */}
          {!shipping && (
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography variant="body2">
                  {t('ecommerceOrders.orderDetailsModal.deliveryAddressNotFound')}
                </Typography>
              </Alert>
            </Grid>
          )}

          {/* Sipariş Edilen Ürünler */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                  {t('ecommerceOrders.orderDetailsModal.orderedProducts', { count: order.items.length })}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {order.items.map((item, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Avatar
                          src={item.image}
                          alt={item.name}
                          variant="rounded"
                          sx={{ width: 60, height: 60 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('ecommerceOrders.orderDetailsModal.quantity')}: {item.quantity}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {orderCurrencySymbol}{item.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', color: ORANGE }}>
                            {t('ecommerceOrders.orderDetailsModal.total')}: {orderCurrencySymbol}{(item.price * item.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('ecommerceOrders.orderDetailsModal.close')}
        </Button>
      </DialogActions>

      {/* ParamPOS Detayları Modal */}
      <Dialog
        open={showParamPosDetails}
        onClose={() => setShowParamPosDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CreditCardIcon color="success" />
            <Box>
              <Typography variant="h6">
                {t('ecommerceOrders.orderDetailsModal.paramPosPaymentDetails')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sipariş #{order.orderNumber}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {parsedParamPosData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Formatted Data */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                    {t('ecommerceOrders.orderDetailsModal.paramPosPaymentInformation')}:
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(parsedParamPosData).map(([key, value]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">
                            {key}:
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace">
                            {value || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Alert severity="warning">
              {t('ecommerceOrders.orderDetailsModal.paramPosDataNotFound')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowParamPosDetails(false)}>
            {t('ecommerceOrders.orderDetailsModal.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default OrderDetailsModal;
